import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { movies, categories } from '@/mocks/movies';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';

type SortOption = 'default' | 'new' | 'rating' | 'popular';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [sortBy, setSortBy] = useState<SortOption>('default');

  useEffect(() => {
    setSortBy('default');
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === slug);

  const filteredMovies = useMemo(() => {
    let result = [...movies];

    if (slug && slug !== 'all') {
      const catName = currentCategory?.name;
      if (catName) {
        result = result.filter((m) => m.categories.includes(catName));
      }
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (m) =>
          m.title.toLowerCase().includes(q) ||
          m.titleEn.toLowerCase().includes(q) ||
          m.director.toLowerCase().includes(q) ||
          m.actors.some((a) => a.toLowerCase().includes(q))
      );
    }

    switch (sortBy) {
      case 'new':
        result.sort((a, b) => b.year - a.year);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'popular':
        result.sort((a, b) => b.viewCount - a.viewCount);
        break;
      default:
        break;
    }

    return result;
  }, [slug, currentCategory, searchQuery, sortBy]);

  const pageTitle = currentCategory
    ? `${currentCategory.name} - 高清在线观看 | 8090影院`
    : searchQuery
    ? `搜索"${searchQuery}"的结果 - 8090影院`
    : '全部电影 - 高清在线观看 | 8090影院';
  const pageDesc = currentCategory
    ? `8090影院${currentCategory.name}专区，海量${currentCategory.name}高清在线观看，无广告不缓冲，畅享精彩${currentCategory.name}电影。`
    : '8090影院全部电影库，国内外大片免费高清在线观看，分类浏览，随心点播。';
  const pageKeywords = currentCategory
    ? `${currentCategory.name},${currentCategory.name}电影,高清${currentCategory.name},免费${currentCategory.name}在线观看,8090影院`
    : '全部电影,免费电影,高清电影在线观看,8090影院,电影库';

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    ...(searchQuery
      ? [{ label: '全部电影', href: '/category/all' }, { label: `搜索: ${searchQuery}` }]
      : currentCategory
      ? [{ label: currentCategory.name }]
      : [{ label: '全部电影' }]),
  ];

  const sortOptions: { value: SortOption; label: string }[] = [
    { value: 'default', label: '默认排序' },
    { value: 'new', label: '最新上架' },
    { value: 'rating', label: '评分最高' },
    { value: 'popular', label: '人气最高' },
  ];

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
      />

      <div className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <div className="w-full px-4 md:px-6 py-6">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-foreground-100 font-heading">
                  {currentCategory ? currentCategory.name : searchQuery ? `搜索: ${searchQuery}` : '全部电影'}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共找到 <strong className="text-primary-500">{filteredMovies.length}</strong> 部电影
                </p>
              </div>

              <div className="flex items-center gap-2">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                      sortBy === opt.value
                        ? 'bg-primary-500 text-background-950'
                        : 'bg-background-100 text-foreground-400 hover:text-foreground-200 hover:bg-background-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {slug !== 'all' && !searchQuery && (
              <div className="flex items-center gap-2 mb-6 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-950'
                        : 'bg-background-100 text-foreground-400 hover:text-foreground-200 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            )}

            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {filteredMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-foreground-500 text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-300 mb-2">没有找到相关电影</h3>
                <p className="text-sm text-foreground-500 mb-6">试试其他关键词或浏览全部分类</p>
                <Link
                  to="/category/all"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-950 font-medium rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  查看全部电影
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}